import { useEffect, useRef, useState } from "react";
import { getPreQuote } from "../services/api";
import type { IPreQuoteRequest, IEstimateResult } from "../services/api";
import { getEvents, safeCapture } from "../analytics";

/**
 * Debounced, deduped pre-quote loader. Re-fetches whenever the (stringified)
 * input changes, after a short debounce so we don't fire a request on every
 * keystroke while the user types the amount.
 *
 * A module-level cache (keyed by the stable input signature) is shared across
 * every hook instance so the TransferPage and the payment-method slot converge
 * on the same result without a second roundtrip. In-flight requests are deduped
 * via a parallel map.
 *
 * Pass `null` to disable the hook (e.g. before the user has entered an amount).
 */

const DEBOUNCE_MS = 500;

const cache = new Map<string, IEstimateResult>();
const inFlight = new Map<string, Promise<IEstimateResult>>();

/** Drops every cached pre-quote (e.g. on a stale-catalog recovery). */
export const clearPreQuoteCache = (): void => {
  cache.clear();
  inFlight.clear();
};

const stableKey = (input: IPreQuoteRequest): string => {
  const entries = Object.entries(input).sort(([a], [b]) => a.localeCompare(b));
  const parts: string[] = [];
  for (const [key, value] of entries) {
    if (value === undefined || value === null || value === "") continue;
    parts.push(`${key}=${String(value)}`);
  }
  return parts.join("&");
};

const fetchWithCache = (
  key: string,
  input: IPreQuoteRequest
): Promise<IEstimateResult> => {
  const cached = cache.get(key);
  if (cached) return Promise.resolve(cached);
  const existing = inFlight.get(key);
  if (existing) return existing;
  const promise = getPreQuote(input)
    .then((result) => {
      cache.set(key, result);
      inFlight.delete(key);
      const events = getEvents();
      const best = result.summary.best;
      safeCapture(events.PRE_QUOTE_CREATED, {
        success: true,
        options_count: result.summary.count,
        best_capability_key: best?.capabilityKey,
        send_amount: best?.sendAmount,
        receive_amount: best?.receiveAmount,
        customer_fee: best?.customerFee,
        customer_fx_rate: best?.customerFxRate,
        payer_code: best?.payerCode,
        corridor_code: best?.corridorCode,
        funding_method: best?.fundingMethod ?? input.fundingMethod,
        payout_method: best?.payoutMethod ?? input.payoutMethod,
        indicative: true,
        from_country: input.originCountryCode,
        to_country: input.destinationCountryCode,
        amount: input.amount,
        anchor: input.anchor ?? "send",
      });
      safeCapture(events.ROUTE_DETECTED, {
        available: result.summary.count > 0,
        from_country: input.originCountryCode,
        to_country: input.destinationCountryCode,
        amount: input.amount,
      });
      return result;
    })
    .catch((err) => {
      inFlight.delete(key);
      safeCapture(getEvents().PRE_QUOTE_CREATED, {
        success: false,
        error: err instanceof Error ? err.message : "Pre-quote failed",
        error_code: "PRE_QUOTE_FAILED",
        from_country: input.originCountryCode,
        to_country: input.destinationCountryCode,
        amount: input.amount,
      });
      throw err;
    });
  inFlight.set(key, promise);
  return promise;
};

export interface UsePreQuoteState {
  result: IEstimateResult | null;
  loading: boolean;
  error: Error | null;
}

export const usePreQuote = (
  input: IPreQuoteRequest | null
): UsePreQuoteState => {
  const key = input ? stableKey(input) : null;

  const [state, setState] = useState<UsePreQuoteState>(() => {
    if (!key) return { result: null, loading: false, error: null };
    const cached = cache.get(key);
    return cached
      ? { result: cached, loading: false, error: null }
      : { result: null, loading: true, error: null };
  });

  // Keep the latest input around so the debounced callback fetches with the
  // value that matches `key` at fire time.
  const inputRef = useRef(input);
  inputRef.current = input;

  useEffect(() => {
    if (!key || !inputRef.current) {
      setState({ result: null, loading: false, error: null });
      return;
    }

    // Serve cache synchronously so a remount (e.g. opening the payment slot)
    // doesn't flash a spinner for an already-resolved estimate.
    const cached = cache.get(key);
    if (cached) {
      setState({ result: cached, loading: false, error: null });
      return;
    }

    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    const timer = window.setTimeout(() => {
      fetchWithCache(key, inputRef.current as IPreQuoteRequest)
        .then((result) => {
          if (!cancelled) setState({ result, loading: false, error: null });
        })
        .catch((err: Error) => {
          if (!cancelled) setState({ result: null, loading: false, error: err });
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // `key` encodes every relevant input — it's the single source of truth for
    // cache identity, so we intentionally omit `input` from the dep list.
  }, [key]);

  return state;
};
